
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import type { FileMetadata, HeliaNode } from '@/types/poc/helia';
import type { FileStoreService } from '@/services/abstract/poc/FileStoreService';
import type { UserService } from '@/services/abstract/UserService';
import type { RecipientEncryptionResult } from '@/types/poc/auth';
import type { MakeFileAvailableToHAPPayloadDTO } from '@/types/SixDetailDTOs';
import type { Logger } from '@/types/Logger';
import { normalizeCid } from '@/services/poc/cidUtils';
import { FileEncryptionService } from '@/services/abstract/poc/fileEncryption.service.interface';
import { hexToBytes } from '@/utils/hex.utils';
import { addFileMetadata, setUploadProgress } from '@/store/slices/poc/heliaSlice';

interface RecipientPublicKey {
  id: string;
  publicKey: Uint8Array;
}

const PROGRESS_ENCRYPTING_START = 0;
const PROGRESS_UPLOADING_START = 30;
const PROGRESS_ANNOUNCING = 80;

/**
 * Stores each recipient's encryption data as a JSON key-blob in Helia and returns
 * a `userId -> CID` map (the `decryptionCIDS` the read-model serves). Ported from
 * the old HAP's `buildEncryptedFileKeys` — the transformation that used to run
 * server-side now happens in the browser before the API handoff.
 *
 * The blob is written with `FileEncryptionService.serializeRecipientKeyBlob` and
 * read back on the download path with `parseRecipientKeyBlob`
 * (`downloadAndDecryptFileThunk.ts`) — that round-trip is the contract.
 */
async function buildEncryptedFileKeys(
  helia: HeliaNode,
  fileStoreService: FileStoreService,
  recipientResults: readonly RecipientEncryptionResult[]
): Promise<Record<string, string>> {
  const encryptedFileKeys: Record<string, string> = {};

  for (const result of recipientResults) {
    const keyFile = new File(
      [FileEncryptionService.serializeRecipientKeyBlob(result)],
      `${result.recipientId}.key.json`,
      { type: 'application/json' }
    );
    const addResult = await fileStoreService.addFile(helia, keyFile);
    encryptedFileKeys[result.recipientId] = normalizeCid(addResult.cid);
  }

  return encryptedFileKeys;
}

/**
 * Resolves ML-KEM encapsulation keys for every recipient of an upload.
 *
 * The uploader is always unioned into the recipient set so they can decrypt
 * their own file regardless of the SIX participant list. A participant whose
 * key cannot be fetched is dropped — the file-key facilitation flow backfills
 * them later — but a missing *uploader* key is unrecoverable and throws.
 */
async function resolveRecipientPublicKeys(args: {
  userService: UserService;
  log: Logger;
  sixId: string;
  allParticipantIds: string[];
  uploaderId: string;
}): Promise<RecipientPublicKey[]> {
  const { userService, log, sixId, allParticipantIds, uploaderId } = args;
  const effectiveParticipantIds = Array.from(new Set([...allParticipantIds, uploaderId]));

  const resolved = await Promise.all(
    effectiveParticipantIds.map(async(participantId) => {
      try {
        const { encapsKey } = await userService.getEncapsKey(participantId);
        const publicKey = new Uint8Array(JSON.parse(encapsKey));
        return { id: participantId, publicKey };
      } catch {
        return null;
      }
    })
  );

  const resolvedRecipients = resolved.filter(
    (recipient): recipient is RecipientPublicKey => recipient !== null
  );
  const resolvedRecipientIds = new Set(resolvedRecipients.map((recipient) => recipient.id));

  const droppedParticipantIds = effectiveParticipantIds.filter(
    (id) => !resolvedRecipientIds.has(id)
  );
  if (droppedParticipantIds.length > 0) {
    log.warn(
      'Some participant encapsulation keys could not be resolved; they will need file-key facilitation',
      { sixId, droppedParticipantIds }
    );
  }
  if (!resolvedRecipientIds.has(uploaderId)) {
    throw new Error(
      'Cannot upload: failed to resolve an encryption key for the uploading user'
    );
  }

  return resolvedRecipients;
}

/**
 * Async thunk for adding an encrypted file to Helia/IPFS
 * Encrypts the file for recipients and announces it to the network
 */
export const addFileToHelia = createAppAsyncThunk(
  'helia/addFile',
  async(
    { sixId, allParticipantIds, file, path, uploadId: providedUploadId, onProgress }: {
      sixId: string;
      allParticipantIds: string[];
      file: File;
      path: string;
      uploadId?: string;
      onProgress?: (loaded: number, total: number) => void;
    },
    {
      extra: { pocServices, userService, sixDetailService, loggerService },
      dispatch,
      getState,
      rejectWithValue,
    }
  ) => {
    const fileName = file.name;
    const uploadId = providedUploadId ?? crypto.randomUUID();
    const log = loggerService.createLogger({ namespace: 'six3ro:p2p:files:upload' });
    const state = getState();

    try {
      // Step 0: Initialize progress
      dispatch(setUploadProgress({
        uploadId,
        fileName,
        path,
        sixId,
        stage: 'encrypting',
        progress: PROGRESS_ENCRYPTING_START
      }));

      // The uploader must always be able to decrypt their own file, regardless
      // of whether they appear in the SIX participant list. Unioning their id
      // into the recipient set replaces the legacy UKEK-wrapped self-access key.
      const uploaderId = state.auth.userId ?? state.user.user?.id;
      if (uploaderId === undefined || uploaderId.trim() === '') {
        throw new Error('Cannot upload: current user id is unavailable');
      }

      // Step 1: Resolve recipient public keys (ML-KEM encapsulation keys)
      const resolvedRecipients = await resolveRecipientPublicKeys({
        userService,
        log,
        sixId,
        allParticipantIds,
        uploaderId,
      });

      // Step 2: Get UKEK and encrypt file for recipients
      const ukek = state.auth.ukek;
      const ukekBytes = hexToBytes(ukek);

      const encryptionResult = await pocServices.fileEncryptionService.encryptFile(
        file,
        ukekBytes,
        resolvedRecipients
      );

      // Update progress: encryption complete, start uploading (animated stage)
      dispatch(setUploadProgress({
        uploadId,
        fileName,
        path,
        sixId,
        stage: 'uploading',
        progress: PROGRESS_UPLOADING_START
      }));

      // Step 3: Add encrypted file and key to IPFS
      const helia = pocServices.p2pStackManagerService.getHeliaNode();
      if(helia === null) {
        throw new Error('Helia node is not initialized');
      }

      const encryptedFileResult = await pocServices.fileStoreService.addFile(
        helia,
        encryptionResult.encryptedFile,
        onProgress
      );
      const encryptedFileKeyResult = await pocServices.fileStoreService.addFile(
        helia,
        encryptionResult.encryptedFileKey
      );

      // Store each recipient's encryption data in Helia, producing the
      // userId -> CID map the backend handoff endpoint expects.
      const encryptedFileKeys = await buildEncryptedFileKeys(
        helia,
        pocServices.fileStoreService,
        encryptionResult.recipientResults
      );

      // Update progress: IPFS upload complete, announcing to network
      dispatch(setUploadProgress({
        uploadId,
        fileName,
        path,
        sixId,
        stage: 'announcing',
        progress: PROGRESS_ANNOUNCING
      }));

      // Step 4: Create metadata with encryption info
      const cidString = normalizeCid(encryptedFileResult.cid);
      const metadata: FileMetadata = {
        id: cidString, // Use CID as ID for P2P-created files
        cid: cidString,
        name: encryptionResult.originalFileName,
        size: encryptionResult.originalFileSize,
        type: encryptionResult.originalFileType,
        lastModified: Date.now(),
        addedBy: uploaderId,
        sixId,
        path,
        encryptedFileKeyCid: normalizeCid(encryptedFileKeyResult.cid),
        decryptionCIDS: Object.entries(encryptedFileKeys).map(([userId, cid]) => ({
          userId,
          cid,
        })),
        uploadedByUserId: uploaderId,
        uploadedByUserName: state.user.user?.name ?? 'unknown'
      };

      // Step 5: Persist optimistic local state, then hand the file off to the
      // backend/HAP path. `makeFileAvailableToHAP` resolves once the underlying
      // command completes (transparent CQRS polling) and throws on command
      // failure or timeout, which the catch block surfaces as an upload error.
      dispatch(addFileMetadata(metadata));

      dispatch(setUploadProgress({
        uploadId,
        fileName,
        path,
        cid: metadata.cid,
        stage: 'announcing',
        progress: PROGRESS_ANNOUNCING,
        sixId,
        metadata
      }));

      const handoffPayload: MakeFileAvailableToHAPPayloadDTO = {
        cid: metadata.cid,
        name: metadata.name,
        size: metadata.size,
        type: metadata.type,
        path: metadata.path,
        encryptedFileKeys,
      };

      await sixDetailService.makeFileAvailableToHAP(sixId, handoffPayload);

      // Handoff accepted by the backend, but the upload is NOT complete yet — a
      // server peer still has to clone the CID. Leave the entry at 'announcing'
      // and let the broker's fileReplicationProgress messages advance it and the
      // uploadSuccessful message mark it complete. An initial watchdog (armed on
      // this thunk's fulfilled action) errors the upload if the server never
      // responds.

      return { uploadId, metadata };

    } catch (error) {
      log.error('addFileToHeliaThunk failed', {
        uploadId,
        sixId,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });

      dispatch(setUploadProgress({
        uploadId,
        fileName,
        path,
        sixId,
        stage: 'error',
        progress: 0,
        error: error instanceof Error ? error.message : 'Upload failed'
      }));

      const errorMessage = error instanceof Error ? error.message : 'Unknown error adding encrypted file';
      return rejectWithValue({ message: errorMessage });
    }
  }
);
